import React, { useRef, useState } from "react";
import {
  Popover,
  IconButton,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  Collections as PhotoIcon,
  AttachFile as AttachIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import toast from "react-hot-toast";
import { AGENT_ID } from "../../app/config";

const MenuButton = styled(Button)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  width: "100%",
  height: "100%",
}));

const IconWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const AddAttachmentsFixed = ({ activeChat, setMessages, messages }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [locationOpen, setLocationOpen] = useState(false);
  const [location, setLocation] = useState({
    latitude: "",
    longitude: "",
    name: "",
    address: "",
  });

  // options: Photos, Documents, Location
  const menuItems = [
    { icon: PhotoIcon, label: "Photos", key: "image" },
    { icon: AttachIcon, label: "Documents", key: "document" },
    { icon: AttachIcon, label: "Location", key: "location" },
  ];

  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const openFilePicker = (type = "image") => {
    if (type === "image") {
      try {
        fileInputRef.current?.click();
      } catch (e) {
        console.warn("openFilePicker failed", e);
      }
    } else if (type === "document") {
      try {
        docInputRef.current?.click();
      } catch (e) {
        console.warn("openDocPicker failed", e);
      }
    } else if (type === "location") {
      setLocationOpen(true);
    }
    // keep popover around briefly so the click is honored
    setTimeout(() => handleClose(), 120);
  };

  const fileHandler = async (e) => {
    const files = e?.target?.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const toastId = toast.loading(`Uploading ${files.length} image(s)...`);

    const previewItems = Array.from(files).map((file, idx) => ({
      id: `temp-${Date.now()}-${idx}`,
      customer_id: activeChat?.mobile_number,
      message_content: null,
      media_url: URL.createObjectURL(file),
      media_type: "image",
      message_type: "outgoing",
      created_at: new Date().toISOString(),
      created_at_formatted: new Date().toLocaleString(),
      _tempId: `temp-${Date.now()}-${idx}`,
      _uploading: true,
      _fileIndex: idx,
      _objectUrl: URL.createObjectURL(file),
    }));

    setMessages((prev) => [...(prev || []), ...previewItems]);

    try {
      const formData = new FormData();
      formData.append("mobile_no", activeChat?.mobile_number);
      formData.append("agent_id", AGENT_ID);
      Array.from(files).forEach((f) => formData.append("images[]", f));
      formData.append("media_type", "image");

      const res = await axios.post("/sendMediaToCustomer", formData);
      toast.dismiss(toastId);
      if (!res?.data?.error) {
        toast.success(res?.data?.message || "Uploaded");
        const urls =
          res?.data?.data?.s3_urls ||
          res?.data?.data?.media_urls ||
          res?.data?.s3_urls ||
          res?.data?.media_urls ||
          [];
        if (urls.length > 0) {
          setMessages((prev) =>
            prev.map((m) =>
              m._tempId
                ? {
                    ...m,
                    _finalUrl: urls[m._fileIndex] || urls.shift(),
                    _uploading: true,
                  }
                : m
            )
          );
        } else {
          const reload = await axios.get(
            `/user/${activeChat?.mobile_number}/messages`
          );
          if (reload?.data?.data) setMessages(reload.data.data);
        }
      } else {
        toast.error(res?.data?.message || "Server rejected upload");
        // cleanup
        setMessages((prev) => (prev ? prev.filter((m) => !m._tempId) : prev));
      }
    } catch (err) {
      console.error("upload failed", err?.response || err);
      toast.dismiss(toastId);
      toast.error(
        err?.response?.data?.message || err?.message || "Upload failed"
      );
      setMessages((prev) => (prev ? prev.filter((m) => !m._tempId) : prev));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  // documents handler (parallel to image upload)
  const docHandler = async (e) => {
    const files = e?.target?.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const toastId = toast.loading(`Uploading ${files.length} document(s)...`);

    const previewItems = Array.from(files).map((file, idx) => ({
      id: `temp-doc-${Date.now()}-${idx}`,
      customer_id: activeChat?.mobile_number,
      message_content: file.name,
      media_url: null,
      media_type: "document",
      message_type: "outgoing",
      created_at: new Date().toISOString(),
      created_at_formatted: new Date().toLocaleString(),
      _tempId: `temp-doc-${Date.now()}-${idx}`,
      _uploading: true,
      _fileIndex: idx,
      _objectName: file.name,
    }));

    setMessages((prev) => [...(prev || []), ...previewItems]);

    try {
      const formData = new FormData();
      formData.append("mobile_no", activeChat?.mobile_number);
      formData.append("agent_id", AGENT_ID);
      Array.from(files).forEach((f) => formData.append("files[]", f));
      formData.append("media_type", "document");

      const res = await axios.post("/sendMediaToCustomer", formData);
      toast.dismiss(toastId);
      if (!res?.data?.error) {
        toast.success(res?.data?.message || "Uploaded");
        const urls =
          res?.data?.data?.s3_urls || res?.data?.data?.media_urls || [];
        if (urls.length > 0) {
          setMessages((prev) =>
            prev.map((m) =>
              m._tempId
                ? {
                    ...m,
                    _finalUrl: urls[m._fileIndex] || urls.shift(),
                    _uploading: false,
                  }
                : m
            )
          );
        } else {
          const reload = await axios.get(
            `/user/${activeChat?.mobile_number}/messages`
          );
          if (reload?.data?.data) setMessages(reload.data.data);
        }
      } else {
        toast.error(res?.data?.message || "Server rejected upload");
        setMessages((prev) => (prev ? prev.filter((m) => !m._tempId) : prev));
      }
    } catch (err) {
      console.error("upload failed", err?.response || err);
      toast.dismiss(toastId);
      toast.error(
        err?.response?.data?.message || err?.message || "Upload failed"
      );
      setMessages((prev) => (prev ? prev.filter((m) => !m._tempId) : prev));
    } finally {
      setUploading(false);
      if (docInputRef.current) docInputRef.current.value = null;
    }
  };

  // location handler: post coordinates to server which will send via WhatsApp
  const sendLocation = async () => {
    const { latitude, longitude, name, address } = location;
    if (!latitude || !longitude)
      return toast.error("Please enter latitude and longitude");

    const temp = {
      id: `temp-loc-${Date.now()}`,
      customer_id: activeChat?.mobile_number,
      message_content: address || name || `${latitude}, ${longitude}`,
      media_url: null,
      media_type: "location",
      message_type: "outgoing",
      created_at: new Date().toISOString(),
      created_at_formatted: new Date().toLocaleString(),
      _tempId: `temp-loc-${Date.now()}`,
      _uploading: true,
    };

    setMessages((prev) => [...(prev || []), temp]);
    setLocationOpen(false);
    setUploading(true);

    try {
      const res = await axios.post("/sendLocationToCustomer", {
        mobile_no: activeChat?.mobile_number,
        agent_id: AGENT_ID,
        latitude,
        longitude,
        name,
        address,
      });

      if (!res?.data?.error) {
        toast.success(res?.data?.message || "Location sent");
        const reload = await axios.get(
          `/user/${activeChat?.mobile_number}/messages`
        );
        if (reload?.data?.data) setMessages(reload.data.data);
      } else {
        toast.error(res?.data?.message || "Failed to send location");
        setMessages((prev) =>
          prev ? prev.filter((m) => m._tempId !== temp._tempId) : prev
        );
      }
    } catch (err) {
      console.error("send location failed", err?.response || err);
      toast.error(
        err?.response?.data?.message || err?.message || "Location send failed"
      );
      setMessages((prev) =>
        prev ? prev.filter((m) => m._tempId !== temp._tempId) : prev
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        disabled={!activeChat || uploading}
        sx={{ transform: "rotate(45deg)" }}
      >
        <AttachIcon />
      </IconButton>

      <VisuallyHiddenInput
        ref={fileInputRef}
        type="file"
        onChange={fileHandler}
        multiple
        accept="image/*"
      />
      <VisuallyHiddenInput
        ref={docInputRef}
        type="file"
        onChange={docHandler}
        multiple
        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        PaperProps={{ sx: { p: 1, mt: -2 } }}
      >
        <Grid container spacing={2}>
          {menuItems.map((item, i) => (
            <Grid item xs={6} key={i}>
              <MenuButton
                fullWidth
                onClick={() => openFilePicker(item.key)}
                disabled={uploading}
              >
                <IconWrapper>
                  <item.icon fontSize="small" />
                </IconWrapper>
                {uploading ? "Uploading..." : item.label}
              </MenuButton>
            </Grid>
          ))}
        </Grid>
      </Popover>
      {/* location dialog */}
      <Dialog open={locationOpen} onClose={() => setLocationOpen(false)}>
        <DialogTitle>Share location</DialogTitle>
        <DialogContent>
          <TextField
            label="Latitude"
            value={location.latitude}
            onChange={(e) =>
              setLocation((s) => ({ ...s, latitude: e.target.value }))
            }
            fullWidth
            margin="dense"
          />
          <TextField
            label="Longitude"
            value={location.longitude}
            onChange={(e) =>
              setLocation((s) => ({ ...s, longitude: e.target.value }))
            }
            fullWidth
            margin="dense"
          />
          <TextField
            label="Place name (optional)"
            value={location.name}
            onChange={(e) =>
              setLocation((s) => ({ ...s, name: e.target.value }))
            }
            fullWidth
            margin="dense"
          />
          <TextField
            label="Address (optional)"
            value={location.address}
            onChange={(e) =>
              setLocation((s) => ({ ...s, address: e.target.value }))
            }
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationOpen(false)}>Cancel</Button>
          <Button
            onClick={sendLocation}
            variant="contained"
            disabled={uploading}
          >
            Send Location
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddAttachmentsFixed;
